/**
 * ThisDeviceMapsTab — the offline map and routing packages on this computer.
 *
 * TCA-26100804, step 4, completed at closeout. Presentation only: every control
 * keeps the handler and the enable/disable expression it already had, moved but
 * not rewritten. The rollback confirmations remain the existing native dialogs,
 * as the owner decided; nothing here changes what a control does.
 *
 * The map action button appears only when it does something. The disabled
 * "Map action" label it replaces named no action in five of seven states.
 *
 * The status slot covers both packages, so it cannot report Ready on the
 * strength of the map alone — a current map says nothing about routing whose
 * status has not been read. This mirrors the rule `assetSummary` already
 * applies to this tab's summary in the tab list, which the slot contradicted.
 */
import React from 'react';
import { routingIsCurrent } from '@/utils/assetObservation';
import { Map } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { describeRoutingState } from '@/api/offlineRoutingAssets';
import DeviceStatusSlot from './DeviceStatusSlot';
import SupportDetails from './SupportDetails';
import RoutingDataCard from './RoutingDataCard';

const ROLLBACK_NOTE = 'Roll back only if the current package causes problems. A confirmation opens first.';

export default function ThisDeviceMapsTab({
  map = null, mapPhase = 'ready', mapBusy = false, mapOutcome = null, onRefreshMap,
  routingPhase = 'ready', routingBusy = false, routingOutcome = null, onRefreshRouting,
  mapLoading = false,
  mapInstalling = false,
  mapRollingBack = false,
  onInstallMap = null,
  onRollbackMap = null,
  mapActionLabel = null,
  canInstallMap = false,
  canRollbackMap = false,
  statusLabels = {},
  statusDetails = {},
  routing = null,
  routingLoading = false,
  routingInstalling = false,
  routingRollingBack = false,
  onInstallRouting = null,
  onRollbackRouting = null,
  servicesUnavailable = false,
}) {
  const mapLabel = mapLoading
    ? 'Checking…'
    : mapPhase !== 'ready' ? 'Map status unavailable' : statusLabels[map?.status] || 'Unavailable';
  const mapDetail = mapLoading
    ? 'Checking installed and published map versions…'
    : mapPhase !== 'ready' ? 'Current checking is unavailable; displayed versions are last known.' : statusDetails[map?.status] || 'Offline map status is unavailable.';

  // Routing copy comes from the existing shared describer, so the slot and the
  // card cannot drift apart in wording.
  const routingState = describeRoutingState(routing);
  const routingKnown = !routingLoading
    && Boolean(routing)
    && routingPhase === 'ready' && Boolean(routing.local);
  const routingOutstanding = !routingKnown || !routingIsCurrent(routing);
  const mapCurrent = !mapLoading && mapPhase === 'ready' && map?.status === 'current' && map?.local?.installed === true;

  // A package that needs something leads. Routing leads only when the map is
  // settled and routing is both known and actionable; an unread routing status
  // is not news to put at the top, but it does stop the slot claiming Ready.
  const routingLeads = mapCurrent && routingKnown && Boolean(routingState.canInstall);

  const tone = mapLoading ? 'quiet'
    : map?.status === 'missing_local' || map?.status === 'update_available' ? 'amber'
      : mapCurrent
        ? (routingOutstanding ? (routingKnown ? 'amber' : 'quiet') : 'green')
        : 'quiet';

  return (
    <div className="space-y-3" data-testid="this-device-maps-tab">
      <DeviceStatusSlot
        tone={tone}
        title={routingLeads ? routingState.headline : mapLabel}
        detail={routingLeads ? routingState.detail : mapDetail}
        lines={!routingLeads && routingOutstanding && !mapLoading
          ? [routingState.headline]
          : []}
        testId="maps-routing-slot"
      />

      <div
        className="rounded-xl border border-stone-200 bg-white px-4 py-3"
        data-testid="offline-map-status"
      >
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100">
            <Map className="h-4 w-4 text-primary" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-stone-800">Offline map</p>
            <p className="mt-0.5 text-xs text-stone-500">
              Map data for dispatch without a connection.
            </p>
            {!mapLoading && (
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-stone-500">
                <span data-testid="offline-map-installed-version">
                  Installed: {map?.local?.installed === true ? map.local.version : map?.status === 'missing_local' && map?.local?.installed === false ? 'none' : 'unknown'}
                </span>
                <span data-testid="offline-map-published-version">
                  Published: {map?.published?.version || 'unknown'}
                </span>
              </div>
            )}
            {(canInstallMap || mapInstalling) && (
              <Button
                variant="outline"
                size="sm"
                onClick={onInstallMap}
                disabled={mapLoading || mapBusy || mapInstalling || mapRollingBack || mapPhase !== 'ready' || servicesUnavailable}
                className="mt-3 w-full h-9 text-xs"
                data-testid="offline-map-action"
              >
                {mapActionLabel}
              </Button>
            )}
            {canRollbackMap && (
              <div className="mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRollbackMap}
                  disabled={mapLoading || mapBusy || mapInstalling || mapRollingBack || mapPhase !== 'ready' || servicesUnavailable}
                  className="w-full h-9 text-xs border-rose-200 text-rose-700 hover:text-rose-800"
                  data-testid="offline-map-rollback"
                >
                  {mapRollingBack ? 'Restoring…' : 'Rollback map'}
                </Button>
                <p className="mt-1 text-[11px] text-stone-500">{ROLLBACK_NOTE}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {(mapPhase === 'unavailable' || mapOutcome === 'unknown' || mapOutcome === 'handoff') && <div role="status">
        {mapOutcome === 'unknown' ? 'The map change outcome is unconfirmed. Do not repeat it; contact support if it remains unresolved.'
          : mapOutcome === 'handoff' ? 'The map request was handed off; completion is not confirmed.' : 'Current map status could not be read.'}
        <Button variant="outline" disabled={mapLoading} onClick={onRefreshMap}>Refresh map status</Button>
      </div>}
      <div data-testid="offline-routing-card">
        <RoutingDataCard
          updateStatus={routing}
          phase={routingPhase} busy={routingBusy} outcome={routingOutcome} onRefresh={onRefreshRouting}
          loading={routingLoading}
          installing={routingInstalling}
          rollingBack={routingRollingBack}
          onInstall={onInstallRouting}
          onRollback={onRollbackRouting}
          servicesUnavailable={servicesUnavailable}
        />
      </div>

      <SupportDetails
        testId="maps-support-details"
        rows={[
          ['Map installed', map?.local?.installed ? map.local.version : null],
          ['Map published', map?.published?.version || null],
          ['Routing installed', routing?.local?.version || null],
          ['Routing published', routing?.published?.version || null],
        ]}
      />
    </div>
  );
}
